import { useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, Divider, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { grnApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { StatusPill } from "@/components/StatusPill";
import { DetailSkeleton } from "@/components/Skeletons";
import { formatDate } from "@/shared/datetime";

interface GrnLine {
  id: string; itemId: string; itemName: string; sku: string;
  batchNo?: string; expiryDate?: string;
  receivedQty: number; acceptedQty: number; rejectedQty: number;
  unitCost: number; remarks?: string;
  /** Computed locally so we can highlight over-receipt without a backend round-trip. */
  orderedQty?: number;
}

interface GrnDetail {
  id: string; documentNo: string; documentDate: string; status: string;
  purchaseOrderId: string; purchaseOrderNo: string;
  vendorId: string; vendorName: string;
  warehouseId: string;
  deliveryNoteRef?: string; totalAmount: number; notes?: string;
  lines: GrnLine[];
}

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

/**
 * UC-M1-PROC-04: GRN detail. Read-only after Post (state machine immutability rule).
 * S3.5 over-receipt check: any line whose ReceivedQty exceeds 110% of the linked PO line
 * raises a yellow banner; posting requires an explicit approval click in the confirm modal.
 */
export function GrnDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const canPost = useAuth(s => s.hasPermission("procurement.grn.post"));

  const [postOpen, setPostOpen] = useState(false);
  const [overReceiptAck, setOverReceiptAck] = useState(false);

  const query = useQuery<GrnDetail>({
    queryKey: ["grn", id],
    queryFn: () => grnApi.get(id) as Promise<GrnDetail>,
    enabled: !!id,
  });

  const postMutation = useMutation({
    mutationFn: () => grnApi.post(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grn", id] });
      qc.invalidateQueries({ queryKey: ["grns"] });
      setPostOpen(false);
    },
  });

  if (query.isLoading || !query.data) return <DetailSkeleton />;
  const grn = query.data;

  // Over-receipt: server returns orderedQty when available; if missing, fall back to
  // ReceivedQty alone (banner won't appear). >10% tolerance per STATE_MACHINES §3.
  const overReceiptLines = grn.lines.filter(l =>
    l.orderedQty != null && l.receivedQty > l.orderedQty * 1.1);
  const hasOverReceipt = overReceiptLines.length > 0;
  const isDraft = grn.status === "Draft";

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>{grn.documentNo}</Typography>
          <StatusPill label={grn.status} />
        </Stack>
        <Stack direction="row" spacing={1}>
          {isDraft && canPost && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PostAddIcon />}
              onClick={() => { setOverReceiptAck(false); setPostOpen(true); }}
            >
              Post GRN
            </Button>
          )}
        </Stack>
      </Stack>

      {hasOverReceipt && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <b>Over-receipt detected on {overReceiptLines.length} line(s).</b> The state machine allows
          up to 10% over-receipt; anything beyond that requires explicit approval at post time.
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">Header</Typography>
        <Stack direction="row" spacing={4} flexWrap="wrap" sx={{ mt: 1 }}>
          <Stack>
            <Typography variant="caption" color="text.secondary">Date</Typography>
            <Typography>{formatDate(grn.documentDate)}</Typography>
          </Stack>
          <Stack>
            <Typography variant="caption" color="text.secondary">Linked PO</Typography>
            <Typography
              component={RouterLink}
              to={`/procurement/purchase-orders/${grn.purchaseOrderId}`}
              color="primary"
            >
              {grn.purchaseOrderNo}
            </Typography>
          </Stack>
          <Stack>
            <Typography variant="caption" color="text.secondary">Vendor</Typography>
            <Typography>{grn.vendorName}</Typography>
          </Stack>
          {grn.deliveryNoteRef && (
            <Stack>
              <Typography variant="caption" color="text.secondary">Delivery note</Typography>
              <Typography>{grn.deliveryNoteRef}</Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Lines</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell align="right">Received</TableCell>
              <TableCell align="right">Accepted</TableCell>
              <TableCell align="right">Rejected</TableCell>
              <TableCell align="right">Unit cost</TableCell>
              <TableCell align="right">Line total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grn.lines.map(l => {
              const over = l.orderedQty != null && l.receivedQty > l.orderedQty * 1.1;
              return (
                <TableRow key={l.id} sx={over ? { bgcolor: "warning.lighter" } : undefined}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{l.itemName}</Typography>
                    <Typography variant="caption" color="text.secondary">{l.sku}</Typography>
                  </TableCell>
                  <TableCell>{l.batchNo ?? "—"}</TableCell>
                  <TableCell>{l.expiryDate ? formatDate(l.expiryDate) : "—"}</TableCell>
                  <TableCell align="right">{l.receivedQty}{over && " ⚠️"}</TableCell>
                  <TableCell align="right">{l.acceptedQty}</TableCell>
                  <TableCell align="right">{l.rejectedQty}</TableCell>
                  <TableCell align="right">{inr.format(l.unitCost)}</TableCell>
                  <TableCell align="right">{inr.format(l.acceptedQty * l.unitCost)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Divider sx={{ my: 2 }} />
        <Stack direction="row" justifyContent="flex-end" spacing={4}>
          <Stack>
            <Typography variant="caption" color="text.secondary">Total accepted value</Typography>
            <Typography variant="h6" color="primary">{inr.format(grn.totalAmount)}</Typography>
          </Stack>
        </Stack>
      </Paper>

      {grn.notes && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
          <Typography sx={{ whiteSpace: "pre-wrap" }}>{grn.notes}</Typography>
        </Paper>
      )}

      <Stack direction="row" spacing={1}>
        <Button onClick={() => navigate("/procurement/grns")}>Back to list</Button>
      </Stack>

      <Dialog open={postOpen} onClose={() => setPostOpen(false)} maxWidth="sm" fullWidth>
        <ClosableDialogTitle onClose={() => setPostOpen(false)}>Post GRN {grn.documentNo}?</ClosableDialogTitle>
        <DialogContent>
          <DialogContentText>
            Posting writes RECEIPT entries to the inventory ledger and increases on-hand stock at
            <b> {grn.vendorName}</b> warehouse. This is irreversible — corrections must be made via a
            reversing GRN.
          </DialogContentText>
          {hasOverReceipt && (
            <Alert severity="warning" sx={{ mt: 2 }} variant="outlined">
              <Stack spacing={1}>
                <Typography>
                  <b>{overReceiptLines.length} line(s) exceed the 10% over-receipt tolerance.</b>
                  Confirm you have explicit approval before posting.
                </Typography>
                <Button
                  size="small"
                  variant={overReceiptAck ? "contained" : "outlined"}
                  color="warning"
                  onClick={() => setOverReceiptAck(a => !a)}
                >
                  {overReceiptAck ? "✓ Over-receipt acknowledged" : "Acknowledge over-receipt"}
                </Button>
              </Stack>
            </Alert>
          )}
          {postMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(postMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={postMutation.isPending || (hasOverReceipt && !overReceiptAck)}
            startIcon={postMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <PostAddIcon />}
            onClick={() => postMutation.mutate()}
          >
            Post GRN
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
